import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import api, { API, apiError } from "@/lib/api";
import { toast } from "sonner";
import { money, moneyClass, cycleLabel, dateShort } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import { W9Form } from "@/components/W9Form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ShieldCheck, SignOut, Wallet, Clock, CheckCircle, Plus, FilePdf, GraduationCap, ArrowSquareOut } from "@phosphor-icons/react";

function Stat({ label, value, tone, testid }) {
  const c = tone === "success" ? "text-[hsl(var(--success))]" : tone === "warn" ? "text-[hsl(var(--warning))]" : "text-foreground";
  return (
    <div data-testid={testid} className="rounded-sm border border-border bg-card p-5">
      <div className="overline">{label}</div>
      <div className={`mt-1 font-mono text-2xl font-semibold ${c}`}>{value}</div>
    </div>
  );
}

function AccessLink({ label, url, testid }) {
  return (
    <div className="rounded-sm border border-border bg-background p-3">
      <div className="overline mb-1">{label}</div>
      {url ? (
        <a href={url} target="_blank" rel="noreferrer" data-testid={testid}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline break-all">
          {url} <ArrowSquareOut size={14} />
        </a>
      ) : (
        <span data-testid={`${testid}-empty`} className="text-sm text-muted-foreground">Not set yet — ask your admin.</span>
      )}
    </div>
  );
}

export default function RepPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [statements, setStatements] = useState([]);
  const [deals, setDeals] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [connect, setConnect] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [form, setForm] = useState({ customer: "", customer_contact: "", product: "", sale_amount: "", is_recurring: false, terms: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/portal/summary").then((r) => setSummary(r.data));
    api.get("/portal/statements").then((r) => setStatements(r.data));
    api.get("/portal/deals").then((r) => setDeals(r.data));
    api.get("/portal/notifications").then((r) => setNotifications(r.data));
    api.get("/portal/connect/status").then((r) => setConnect(r.data)).catch(() => setConnect(null));
    api.get("/portal/checklist").then((r) => setChecklist(r.data)).catch(() => setChecklist([]));
  };
  useEffect(() => { load(); }, []);

  const doLogout = async () => { await logout(); navigate("/login"); };

  const connectStripe = async () => {
    try { const r = await api.post("/portal/connect/onboard"); window.open(r.data.url, "_blank"); toast.success("Opened Stripe setup in a new tab"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const onChecklistAction = (action) => {
    if (action === "connect") connectStripe();
    if (action === "w9") document.querySelector('[data-testid="portal-w9"]')?.scrollIntoView({ behavior: "smooth" });
  };

  const submit = async () => {
    if (!form.customer || !form.product || !form.sale_amount) { toast.error("Client, product and price are required"); return; }
    setBusy(true);
    try {
      await api.post("/portal/deals", {
        customer: form.customer, customer_contact: form.customer_contact, product: form.product,
        sale_amount: Number(form.sale_amount), is_recurring: form.is_recurring, terms: form.terms,
      });
      toast.success("Submitted — enters pending until payment is collected");
      setForm({ customer: "", customer_contact: "", product: "", sale_amount: "", is_recurring: false, terms: "" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!summary) return <div className="flex h-screen items-center justify-center font-mono text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-[1100px] items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-primary text-primary-foreground"><ShieldCheck size={18} weight="fill" /></div>
            <div>
              <div className="font-heading text-sm font-extrabold">Partner Portal</div>
              <div className="overline text-[9px]">{summary.rep.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/portal/training" data-testid="portal-training-link"
              className="inline-flex items-center gap-1.5 rounded-sm bg-[#102542] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 transition-opacity">
              <GraduationCap size={16} weight="fill" /> Training
            </Link>
            <StatusBadge status={summary.rep.status} />
            <Button data-testid="portal-logout" variant="ghost" size="sm" className="gap-2 text-muted-foreground" onClick={doLogout}><SignOut size={16} /> Sign out</Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1100px] px-6 py-8">
        <h1 className="font-heading mb-6 text-3xl font-black tracking-tight">Your Scorecard</h1>

        {notifications.length > 0 && (
          <div className="mb-6 space-y-2" data-testid="portal-notifications">
            {notifications.map((n, i) => {
              const tone = n.severity === "critical" ? "border-destructive/40 bg-destructive/10 text-destructive"
                : n.severity === "warning" ? "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]"
                : n.severity === "success" ? "border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]"
                : "border-border bg-card text-muted-foreground";
              return <div key={i} data-testid={`notif-${n.type}`} className={`rounded-sm border px-4 py-2.5 text-sm ${tone}`}>{n.message}</div>;
            })}
          </div>
        )}

        {!connect?.payouts_enabled && (
          <div className="mb-6 flex items-center justify-between rounded-sm border border-border bg-card p-4">
            <div className="text-sm">
              <span className="font-medium">Set up payouts</span>
              <span className="text-muted-foreground"> — connect Stripe so we can pay you directly. {connect?.connected ? "Onboarding incomplete." : ""}</span>
            </div>
            <Button data-testid="portal-connect-stripe" variant="outline" size="sm" onClick={connectStripe}>{connect?.connected ? "Continue" : "Connect Stripe"}</Button>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Stat testid="portal-leads" label="Open Leads" value={summary.leads} />
          <Stat testid="portal-closed" label="Closed Deals" value={summary.closed} />
          <Stat testid="portal-revenue" label="Revenue Sold" value={money(summary.revenue)} />
          <Stat testid="portal-owed" label="Owed To You" value={money(summary.commission_owed)} tone="success" />
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3">
          <div className="rounded-sm border border-border bg-card p-4"><div className="overline flex items-center gap-1"><Clock size={12} /> Pending (not collected)</div><div className="mt-1 font-mono text-xl">{money(summary.commission_pending)}</div></div>
          <div className="rounded-sm border border-border bg-card p-4"><div className="overline flex items-center gap-1"><Wallet size={12} /> Payable now</div><div className="mt-1 font-mono text-xl text-primary">{money(summary.commission_owed)}</div></div>
          <div className="rounded-sm border border-border bg-card p-4"><div className="overline flex items-center gap-1"><CheckCircle size={12} /> Paid to date</div><div className="mt-1 font-mono text-xl text-[hsl(var(--success))]">{money(summary.commission_paid)}</div></div>
        </div>

        {checklist.length > 0 && (
          <div className="mt-6">
            <OnboardingChecklist steps={checklist} onAction={onChecklistAction} />
          </div>
        )}

        <div className="mt-6 rounded-sm border border-border bg-card p-5" data-testid="portal-access">
          <div className="mb-3 flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-primary/15 text-primary"><Wallet size={18} weight="fill" /></div>
            <div className="font-heading text-base font-semibold">My Access</div>
          </div>
          {summary.rep.data_source === "votel" ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <AccessLink label="LocalPro OS Login" url={summary.rep.votel_login_url} testid="access-votel-login" />
              <AccessLink label="My Tracking Link" url={summary.rep.votel_tracking_link} testid="access-votel-tracking" />
            </div>
          ) : (
            <div className="text-sm text-muted-foreground" data-testid="access-managed">
              {summary.rep.ghl_sub_account_id
                ? <>Your workspace is provisioned. Sub-account: <span className="font-mono text-foreground">{summary.rep.ghl_sub_account_id}</span>. Ask your admin for the login link.</>
                : "Your workspace access is managed by your admin."}
            </div>
          )}
        </div>

        <div className="mt-6 rounded-sm border border-border bg-card p-5" data-testid="portal-w9">
          <div className="mb-4 flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-primary/15 text-primary"><ShieldCheck size={18} weight="fill" /></div>
            <div>
              <div className="font-heading text-base font-semibold">W-9 / Taxpayer Info</div>
              <p className="text-xs text-muted-foreground">Required before your first payout. Your TIN is encrypted — we only show the last 4.</p>
            </div>
          </div>
          <W9Form base="/portal" defaultName={summary.rep.name} onSaved={load} />
        </div>

        <Tabs defaultValue="submit" className="mt-8">
          <TabsList>
            <TabsTrigger value="submit" data-testid="portal-tab-submit">Submit a Deal</TabsTrigger>
            <TabsTrigger value="deals" data-testid="portal-tab-deals">My Submissions</TabsTrigger>
            <TabsTrigger value="statements" data-testid="portal-tab-statements">Statements</TabsTrigger>
          </TabsList>

          <TabsContent value="submit" className="mt-4">
            <div className="max-w-2xl rounded-sm border border-border bg-card p-6">
              <p className="mb-4 text-sm text-muted-foreground">Submit a new client sale. It's attributed to you and enters <span className="font-medium text-foreground">pending</span> until payment clears — your commission is calculated automatically off your agreement.</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-1.5"><Label className="overline">Client Name</Label><Input data-testid="intake-customer" value={form.customer} onChange={(e) => setForm({ ...form, customer: e.target.value })} placeholder="Acme Co" /></div>
                <div className="space-y-1.5"><Label className="overline">Contact</Label><Input data-testid="intake-contact" value={form.customer_contact} onChange={(e) => setForm({ ...form, customer_contact: e.target.value })} placeholder="joe@acme.com" className="font-mono" /></div>
                <div className="space-y-1.5"><Label className="overline">Product</Label><Input data-testid="intake-product" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} placeholder="CallBoss" /></div>
                <div className="space-y-1.5"><Label className="overline">Price ($)</Label><Input data-testid="intake-amount" type="number" value={form.sale_amount} onChange={(e) => setForm({ ...form, sale_amount: e.target.value })} className="font-mono" placeholder="297" /></div>
                <div className="space-y-1.5"><Label className="overline">Terms</Label><Input data-testid="intake-terms" value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} placeholder="monthly" /></div>
                <div className="col-span-2 flex items-center justify-between rounded-sm border border-border p-3"><span className="text-sm font-medium">Recurring monthly</span><Switch data-testid="intake-recurring" checked={form.is_recurring} onCheckedChange={(v) => setForm({ ...form, is_recurring: v })} /></div>
              </div>
              <Button data-testid="intake-submit" className="mt-4 gap-2" onClick={submit} disabled={busy}><Plus size={16} weight="bold" /> {busy ? "Submitting…" : "Submit Deal"}</Button>
            </div>
          </TabsContent>

          <TabsContent value="deals" className="mt-4">
            <div className="overflow-hidden rounded-sm border border-border">
              <table className="w-full text-sm" data-testid="portal-deals-table">
                <thead className="bg-card"><tr className="border-b border-border">{["Date", "Client", "Product", "Amount", "Payment"].map((c, i) => <th key={c} className={`px-4 py-3 overline ${i === 3 ? "text-right" : "text-left"}`}>{c}</th>)}</tr></thead>
                <tbody>
                  {deals.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-muted-foreground">No submissions yet.</td></tr>}
                  {deals.map((d) => (
                    <tr key={d.id} className="border-b border-border hover:bg-accent transition-colors duration-200">
                      <td className="px-4 py-2.5 font-mono">{dateShort(d.won_date)}</td>
                      <td className="px-4 py-2.5">{d.customer}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{d.product}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{money(d.sale_amount)}</td>
                      <td className="px-4 py-2.5">{d.payment_confirmed ? <span className="font-mono text-xs text-[hsl(var(--success))]">collected</span> : <span className="font-mono text-xs text-[hsl(var(--warning))]">pending</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="statements" className="mt-4">
            <div className="overflow-hidden rounded-sm border border-border">
              <table className="w-full text-sm" data-testid="portal-statements-table">
                <thead className="bg-card"><tr className="border-b border-border">{["Cycle", "Gross", "Adjustments", "Net Payable", "Status", ""].map((c, i) => <th key={i} className={`px-4 py-3 overline ${[1, 2, 3].includes(i) ? "text-right" : "text-left"}`}>{c}</th>)}</tr></thead>
                <tbody>
                  {statements.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-muted-foreground">No statements yet.</td></tr>}
                  {statements.map((s) => (
                    <tr key={s.id} className="border-b border-border hover:bg-accent transition-colors duration-200">
                      <td className="px-4 py-2.5 font-mono">{cycleLabel(s.cycle)}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{money(s.gross_earned)}</td>
                      <td className={`px-4 py-2.5 text-right font-mono ${moneyClass(s.adjustments)}`}>{money(s.adjustments)}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-medium">{money(s.net_payable)}</td>
                      <td className="px-4 py-2.5"><StatusBadge status={s.status} /></td>
                      <td className="px-4 py-2.5 text-right">
                        <button data-testid={`portal-statement-pdf-${s.id}`} onClick={() => window.open(`${API}/portal/statements/${s.id}/pdf`, "_blank")} className="text-primary hover:text-primary/70 transition-colors" title="Download PDF"><FilePdf size={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
